import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { getApiUrl } from "@/utils/api";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/utils/toast";
import { Company, Facility } from "@/types";
import { t } from "i18next";

interface CompaniesResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Company[];
}
interface FacilitiesResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Facility[];
}

interface UsersFormProps {
  userId?: number;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const createUserSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  rol: z.enum(["admin", "client", "facility"]),
  company: z.number().nullable().optional(),
  facility: z.number().nullable().optional(),
});

const editUserSchema = z.object({
  email: z.string().email("Invalid email address"),
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  rol: z.enum(["admin", "client", "facility"]),
  company: z.number().nullable().optional(),
  facility: z.number().nullable().optional(),
});

type CreateUserFormValues = z.infer<typeof createUserSchema>;
type EditUserFormValues = z.infer<typeof editUserSchema>;

export default function UsersForm({
  userId,
  onSuccess,
  onCancel,
}: UsersFormProps) {
  const { user, token } = useAuth();
  const queryClient = useQueryClient();

  // Use different form types based on whether we're editing or creating
  const form = useForm<CreateUserFormValues | EditUserFormValues>({
    resolver: zodResolver(userId ? editUserSchema : createUserSchema),
    defaultValues: {
      email: "",
      first_name: "",
      last_name: "",
      rol: "client",
      company: user?.company?.id || null,
      facility: user?.facility?.id || null,
      ...(userId ? {} : { password: "" }),
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ["user", userId],
    queryFn: async () => {
      if (!userId) return null;
      const response = await fetch(getApiUrl(`users/${userId}/`), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch user");
      }
      return response.json();
    },
    enabled: !!userId,
  });

  useEffect(() => {
    if (data) {
      form.reset({
        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name,
        rol: data.rol,
        company: data.company?.id || user?.company?.id || null,
        facility: data.facility?.id || user?.facility?.id || null,
      });
    }
  }, [data, form]);

  // Fetch companies for dropdown
  const { data: companiesData } = useQuery<CompaniesResponse>({
    queryKey: ["companies"],
    queryFn: async () => {
      const response = await fetch(getApiUrl("companies/"), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch  companies");
      }
      return response.json();
    },
  });

  // Fetch facilities for dropdown
  const { data: facilitiesData } = useQuery<FacilitiesResponse>({
    queryKey: ["facilities"],
    queryFn: async () => {
      const response = await fetch(
        getApiUrl(`facilities/?company=${user?.company?.id}`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        throw new Error("Failed to fetch companies");
      }
      return response.json();
    },
  });

  const mutation = useMutation({
    mutationFn: async (values: CreateUserFormValues | EditUserFormValues) => {
      const url = userId ? getApiUrl(`users/${userId}/`) : getApiUrl("users/");
      const method = userId ? "PATCH" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to save user");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success(`User ${userId ? "updated" : "created"} successfully`);
      setTimeout(() => {
        onSuccess?.();
      }, 1000); // Add a delay to ensure state is updated
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const onSubmit = (values: CreateUserFormValues | EditUserFormValues) => {
    mutation.mutate(values);
  };

  // Watch for role changes and update company field accordingly
  const roleValue = form.watch("rol");

  useEffect(() => {
    // If role is admin, set company to null
    if (roleValue === "admin") {
      form.setValue("company", null);
    }
  }, [roleValue, form]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col h-full pb-24 overflow-y-auto"
      >
        <div className="space-y-6 overflow-y-auto flex-1 px-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("users.fields.email")}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder={t("users.fields.email")}
                    {...field}
                    readOnly={!!userId}
                    className={userId ? "bg-muted" : ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {!userId && (
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("users.fields.password")}</FormLabel>
                  <FormControl>
                    <Input type="password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="first_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("users.fields.first_name")}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="last_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("users.fields.last_name")}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="rol"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("users.fields.rol")}</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a role" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {user?.rol === "admin" && (
                      <SelectItem value="admin">Admin</SelectItem>
                    )}
                    {["admin", "client"].includes(user?.rol) && (
                      <SelectItem value="client">Client</SelectItem>
                    )}
                    {/* <SelectItem value="facility">Facility</SelectItem> */}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="company"
            render={({ field }) =>
              // Only show company field if role is not admin
              roleValue !== "admin" ? (
                <FormItem>
                  <FormLabel>{t("users.fields.company")}</FormLabel>
                  <Select
                    onValueChange={(value) =>
                      field.onChange(value === "none" ? null : parseInt(value))
                    }
                    value={field.value?.toString() || "none"}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a company" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {companiesData?.results.map((company: Company) => (
                        <SelectItem
                          key={company.id}
                          value={company.id.toString()}
                        >
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              ) : (
                <></>
              )
            }
          />

          {/* <FormField
            control={form.control}
            name="facility"
            render={({ field }) =>
              // Only show facility field if role is not admin
              roleValue !== "admin" ? (
                <FormItem>
                  <FormLabel>{t("users.fields.facility")}</FormLabel>
                  <Select
                    onValueChange={(value) =>
                      field.onChange(value === "none" ? null : parseInt(value))
                    }
                    value={field.value?.toString() || "none"}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a facility" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {facilitiesData?.results.map((facility: Facility) => (
                        <SelectItem
                          key={facility.id}
                          value={facility.id.toString()}
                        >
                          {facility.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              ) : null
            }
          /> */}
        </div>

        <div className="fixed inset-x-0 bottom-0 bg-background p-4 border-t flex justify-end space-x-2 mt-auto">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="default" type="submit" loading={mutation.isLoading}>
            {userId ? t("common.update") : t("common.create")}
          </Button>
        </div>
      </form>
    </Form>
  );
}
